import { formatDateIn, formatMoneyIn, type Locale } from '@zuruflex/i18n';
import type { Promotion, PromotionRedemptionStatus } from '@zuruflex/types';
import type { TFunction } from 'i18next';

/*
 * The small readings of a promotion that the list, the detail page and the
 * dialog all need, kept in one place so "20% off, up to ₵100" is phrased
 * the same way wherever it appears.
 */

export const REDEMPTION_VARIANT: Record<
  PromotionRedemptionStatus,
  'success' | 'warning' | 'default'
> = {
  CONFIRMED: 'success',
  RESERVED: 'warning',
  RELEASED: 'default',
};

/**
 * Where a promotion actually stands, which is not the same as its status.
 *
 * An ACTIVE promo whose dates have not come round, or have passed, applies
 * to nothing — and a list that calls it "Active" invites the question of
 * why nobody is getting the discount.
 */
export type PromotionState = 'live' | 'scheduled' | 'ended' | 'paused' | 'archived';

export function promotionState(promo: Promotion, now = new Date()): PromotionState {
  if (promo.status === 'ARCHIVED') return 'archived';
  if (promo.status === 'PAUSED') return 'paused';
  if (new Date(promo.startsAt) > now) return 'scheduled';
  // `endsAt` is exclusive, and so is used-up: both mean nothing more applies.
  if (promo.endsAt && new Date(promo.endsAt) <= now) return 'ended';
  if (promo.maxRedemptions !== null && promo.redemptionCount >= promo.maxRedemptions) {
    return 'ended';
  }
  return 'live';
}

export const STATE_VARIANT: Record<
  PromotionState,
  'success' | 'info' | 'warning' | 'default' | 'outline'
> = {
  live: 'success',
  scheduled: 'info',
  paused: 'warning',
  ended: 'default',
  archived: 'outline',
};

/** "20% off", "20% off, up to ₵100.00" or "₵50.00 off". */
export function promotionValue(promo: Promotion, t: TFunction, locale: Locale): string {
  if (promo.discountType === 'FIXED') {
    return t('admin:promotions.value.amount', {
      amount: formatMoneyIn(promo.amountOff ?? 0, promo.currency, locale),
    });
  }
  const percent = percentFromBps(promo.percentBps ?? 0);
  return promo.maxDiscount !== null
    ? t('admin:promotions.value.percentCapped', {
        percent,
        cap: formatMoneyIn(promo.maxDiscount, promo.currency, locale),
      })
    : t('admin:promotions.value.percent', { percent });
}

/** 1250 → "12.5". Basis points in the database, percent on the screen. */
export function percentFromBps(bps: number): string {
  return String(Math.round(bps) / 100);
}

/**
 * The dates a promo runs, as a person would say them. `endsAt` is exclusive,
 * so the last day shown is the day before it.
 */
export function promotionWindow(promo: Promotion, t: TFunction, locale: Locale): string {
  const start = formatDateIn(promo.startsAt, locale);
  if (!promo.endsAt) return t('admin:promotions.window.open', { start });
  return t('admin:promotions.window.range', {
    start,
    end: formatDateIn(lastDay(promo.endsAt), locale),
  });
}

/** The last day a promo runs, from its exclusive end. */
export function lastDay(endsAt: string): Date {
  return new Date(new Date(endsAt).getTime() - 1);
}

/** "3 of 100", or "3 · no limit". */
export function promotionUses(promo: Promotion, t: TFunction): string {
  return promo.maxRedemptions === null
    ? t('admin:promotions.uses.unlimited', { used: promo.redemptionCount })
    : t('admin:promotions.uses.of', { used: promo.redemptionCount, max: promo.maxRedemptions });
}
